import { isAxiosError } from 'axios';
import { AmethystClient } from '@/integrations/Immersve/AmethystClient';
import type { Whoami } from '@/integrations/Immersve/auth/types';
import { ImmersveError } from '@/common/ImmersveError';
import { config } from '@/config';
import { authFlow } from '@/integrations/Immersve/auth/AuthFlow';

/** GetAmlReviewsFailed, from getAmlReviews — the shape fluorite's clients name. */
function failureName(method: string): string {
  return `${method[0]?.toUpperCase()}${method.slice(1)}Failed`;
}

type ApiMethod = (...args: never[]) => Promise<unknown>;

// Failures arrive from the client as axios threw them, and are named here:
// a 401 is the one this layer knows the meaning of, and also decides what
// follows — under api-key auth there is no flow to send anyone into, so the
// error is all the caller gets. Everything else is named after the call that
// failed, which is what groups it in error tracking, and carries the axios
// error as its cause. That is @centrapay/axios-verror's enhance in all but
// its info bag — and like enhance, an error with no request to describe is
// left alone.
function handlingFailures<Methods extends Record<string, ApiMethod>>(methods: Methods): Methods {
  const guarded = Object.entries(methods).map(([name, method]) => [
    name,
    async (...args: never[]) => {
      try {
        return await method(...args);
      } catch (err) {
        if (!isAxiosError(err) || !err.config) {
          throw err;
        }
        if (err.response?.status === 401) {
          if (config.authType === 'cookie') {
            authFlow.redirectToSignIn();
          }
          throw ImmersveError.unauthorised({ cause: err });
        }
        throw ImmersveError.fromCause({ name: failureName(name), cause: err });
      }
    },
  ]);
  return Object.fromEntries(guarded) as Methods;
}

// Exempt from the redirect above: whoami asks whether a session exists, so a
// 401 is its answer, and bouncing into the sign-in flow on it would loop.
async function probeSession(client: AmethystClient): Promise<Whoami | undefined> {
  try {
    return await client.getWhoami();
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 401) {
      return undefined;
    }
    throw err;
  }
}

export function useImmersveApiClient() {
  const client = new AmethystClient();

  return {
    getWhoami: () => probeSession(client),
    postLogout: client.postLogout.bind(client),
    ...handlingFailures({
      getAmlReviews: client.getAmlReviews.bind(client),
      getAmlReviewById: client.getAmlReviewById.bind(client),
      getAmlReviewResults: client.getAmlReviewResults.bind(client),
      getAmlReviewResult: client.getAmlReviewResult.bind(client),
      getLatestHumanReview: client.getLatestHumanReview.bind(client),
      getAmlReviewAttachment: client.getAmlReviewAttachment.bind(client),
      getAmlReviewAttachmentJson: client.getAmlReviewAttachmentJson.bind(client),
      getFrankieOneKycCheck: client.getFrankieOneKycCheck.bind(client),
      getSumsubKycCheck: client.getSumsubKycCheck.bind(client),
      createHumanReview: client.createHumanReview.bind(client),
      updateHumanReview: client.updateHumanReview.bind(client),
      completeAmlReview: client.completeAmlReview.bind(client),
      listCardTokens: client.listCardTokens.bind(client),
      listCards: client.listCards.bind(client),
      getCard: client.getCard.bind(client),
      listFundingSources: client.listFundingSources.bind(client),
      getFundingSource: client.getFundingSource.bind(client),
      getPayment: client.getPayment.bind(client),
      releasePaymentAuthorization: client.releasePaymentAuthorization.bind(client),
    }),
  };
}

export type ImmersveApiClient = ReturnType<typeof useImmersveApiClient>;
